import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { PlaybookCreateRequest, PlaybookInDb, PlaybookStatus, PlaybookUpdate } from '@/api';
import { PlaybooksService, type PlaybookListQuery } from '@/services/playbooks/playbooksService';

export const usePlaybooksStore = defineStore('playbooks', () => {
  const playbooks = ref<PlaybookInDb[]>([]);
  const totalCount = ref<number>(0);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  // Values the server actually has for each filterable column, so filter
  // controls only offer what exists in the org. Keys are absent for columns the
  // response says nothing about, hence the `| undefined`.
  const filterOptions = ref<Record<string, (string | null)[] | undefined>>({});

  async function fetchPlaybooks(query: PlaybookListQuery = {}): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const result = await PlaybooksService.search(query);
      playbooks.value = result.results;
      totalCount.value = result.result_count;
      filterOptions.value = result.filter_options as Record<string, (string | null)[] | undefined>;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to fetch playbooks';
      throw error_;
    } finally {
      loading.value = false;
    }
  }

  async function createPlaybook(payload: PlaybookCreateRequest): Promise<PlaybookInDb> {
    const playbook = await PlaybooksService.create(payload);
    upsertPlaybook(playbook);
    return playbook;
  }

  async function createPlaybookFromTemplate(templateKey: string): Promise<PlaybookInDb> {
    const playbook = await PlaybooksService.createFromTemplate(templateKey);
    upsertPlaybook(playbook);
    return playbook;
  }

  async function updatePlaybook(uid: string, payload: PlaybookUpdate): Promise<PlaybookInDb> {
    const playbook = await PlaybooksService.update(uid, payload);
    upsertPlaybook(playbook);
    return playbook;
  }

  /** Archive / restore: there is no dedicated endpoint, status is a plain update. */
  async function setPlaybookStatus(uid: string, status: PlaybookStatus): Promise<PlaybookInDb> {
    return updatePlaybook(uid, { status });
  }

  async function deletePlaybook(uid: string): Promise<void> {
    await PlaybooksService.delete(uid);
    playbooks.value = playbooks.value.filter(playbook => playbook.uid !== uid);
    totalCount.value = Math.max(0, totalCount.value - 1);
  }

  function upsertPlaybook(playbook: PlaybookInDb): void {
    const index = playbooks.value.findIndex(existing => existing.uid === playbook.uid);
    if (index === -1) {
      playbooks.value = [playbook, ...playbooks.value];
      totalCount.value += 1;
    } else {
      playbooks.value[index] = playbook;
    }
  }

  return {
    playbooks,
    totalCount,
    loading,
    error,
    filterOptions,
    fetchPlaybooks,
    createPlaybook,
    createPlaybookFromTemplate,
    updatePlaybook,
    setPlaybookStatus,
    deletePlaybook,
    upsertPlaybook,
  };
});
